import React, { PureComponent, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import difference from 'lodash.difference';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import FormFieldLabel from 'components/FormFieldLabel';
import Icon from 'components/commons/Icon';
import Option from '../Option';

import {
  Wrapper,
  AutoCompleteInput,
  InnerAutoCompleteWrapper,
  InnerAutoCompleteInput,
  ToggleButtonLabel,
  ToggleButton,
  OptionsList,
  OptionsLabel,
  ToggleButtonIcon,
  CleanBtn,
  Divider,
  HiddenLinks,
  ToggleButtonActions,
  MobileSelect,
} from './styles';

class Select extends PureComponent {
  wrapperRef = createRef();

  toggleBtnRef = createRef();

  autoCompleteInputRef = createRef();

  mobileSelectRef = createRef();

  options = {};

  constructor(props) {
    super(props);
    this.state = {
      inputValue: '',
      isOpen: false,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.handleWindowClick);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleWindowClick);
  }

  handleWindowClick = event => {
    const { closeOnClickOutside } = this.props;
    const { isOpen } = this.state;

    if (
      isOpen &&
      closeOnClickOutside &&
      this.wrapperRef.current !== event.target &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.close();
    }
  };

  toggle = () => {
    const { isOpen } = this.state;

    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    this.setState(() => ({
      isOpen: true,
    }));
  };

  close = () => {
    this.setState(() => ({
      inputValue: '',
      isOpen: false,
    }));
  };

  getValueArray = () => {
    const { value, multiple, multipleValueSeparator } = this.props;

    if (Array.isArray(value)) {
      return value;
    }

    if (multiple) {
      return value.split(multipleValueSeparator);
    }
    return [value];
  };

  getIsOptionChecked = (optionValue = '') => {
    const valueArray = this.getValueArray();

    if (Array.isArray(optionValue)) {
      return difference(optionValue, valueArray).length === 0;
    }

    return valueArray.includes(optionValue);
  };

  getOptionByValue = (value, options) => {
    const { resetOption } = this.props;

    if (resetOption && resetOption.value === value) {
      return resetOption;
    }

    return options.reduce((result, option) => {
      if (result) {
        return result;
      }

      if (option.value === value) {
        return option;
      }

      if (option.options) {
        return this.getOptionByValue(value, option.options);
      }

      return null;
    }, null);
  };

  getToggleLabel = (resetOption, groupOptions) => {
    const { options, defaultButtonLabel, optionsStyleType } = this.props;

    const valueArray = this.getValueArray();

    if (optionsStyleType === 'checkbox') {
      const checkedGroupOptions = groupOptions.filter(({ checked }) => checked);

      if (resetOption.checked) {
        return { toggleLabel: resetOption.label, hiddenLinksCount: 0 };
      }
      if (checkedGroupOptions.length === 1) {
        const groupOption = checkedGroupOptions[0];
        const hiddenLinksCount = valueArray.length - groupOption.value.length;

        return { toggleLabel: groupOption.label, hiddenLinksCount };
      }
    }

    const valueArrayLabel = valueArray.reduce((result, item) => {
      const itemOption = this.getOptionByValue(item, options);
      if (itemOption) {
        result.push(itemOption.label || itemOption.value || '');
      }

      return result;
    }, []);

    return {
      toggleLabel: valueArrayLabel[0] || defaultButtonLabel,
      hiddenLinksCount: valueArrayLabel.length - 1,
    };
  };

  getOptionsToShow = options => {
    const { name } = this.props;
    const { inputValue } = this.state;

    return options.reduce(
      (result, { label, value, options: subOptions = [], ...option }) => {
        const isMatch = label
          .toLowerCase()
          .includes(inputValue.toLowerCase().trim());

        const subOptionsToShow =
          subOptions && subOptions.length > 0
            ? this.getOptionsToShow(subOptions)
            : [];

        if (!isMatch && subOptionsToShow.length === 0) {
          return result;
        }

        return [
          ...result,
          {
            ...option,
            label,
            value,
            checked: this.getIsOptionChecked(value),
            name,
            onRequestSelect: this.handleOptionSelect,
            options: subOptionsToShow,
          },
        ];
      },
      []
    );
  };

  handleInputChange = inputValue => {
    this.setState(() => ({
      inputValue,
    }));
  };

  handleOptionSelect = event => {
    const { onRequestChange, value } = this.props;
    const { value: optionValue, href } = event.currentTarget.dataset;

    if (value !== optionValue) {
      onRequestChange(href || optionValue);
    }
  };

  handleMobileSelectChange = event => {
    const { onRequestChange } = this.props;
    const { value } = event.target;

    const link = `/?converter-type=${value}`;

    this.mobileSelectRef.current.blur();

    onRequestChange(link);
  };

  handleOptionKeyDown = event => {
    const { keyCode, target } = event;
    const prevOptionRef = this.options[
      `${parseInt(target.dataset.index, 10) - 1}ref`
    ];
    const nextOptionRef = this.options[
      `${parseInt(target.dataset.index, 10) + 1}ref`
    ];

    if (keyCode === 40 && nextOptionRef) {
      nextOptionRef.current.focus();
    } else if (keyCode === 38) {
      if (prevOptionRef) {
        prevOptionRef.current.focus();
      } else if (this.autoCompleteInputRef.current) {
        this.autoCompleteInputRef.current.focus();
      } else if (this.toggleBtnRef.current) {
        this.toggleBtnRef.current.focus();
      }
    }
  };

  handleOptionKeyUp = event => {
    const { keyCode } = event;

    if (keyCode === 13) {
      event.target.click();
    }
  };

  handleWrapperKeyUp = event => {
    const { keyCode } = event;

    if (keyCode === 27) {
      this.close();
    }
  };

  handleBlur = event => {
    const { isOpen } = this.state;

    if (!this.wrapperRef.current.contains(event.relatedTarget) && isOpen) {
      this.close();
    }
  };

  handleToggleBtnKeyUp = event => {
    const { keyCode } = event;
    const { isOpen } = this.state;

    const firstOptionRef = this.options[`0ref`];

    if (isOpen && keyCode === 40 && firstOptionRef) {
      firstOptionRef.current.focus();
    }
  };

  handleInputKeyDown = event => {
    const { keyCode } = event;
    const firstOptionRef = this.options[`0ref`];

    if (keyCode === 40 && firstOptionRef) {
      firstOptionRef.current.focus();
    }
  };

  handleListKeyDown = event => {
    const { keyCode } = event;

    if (keyCode === 40 || keyCode === 38) {
      event.preventDefault();
    }
  };

  renderOptions = options => {
    return options.map(({ options: subOptions = [], ...option }, index) => (
      <Fragment key={option.value || option.label || index}>
        {this.renderOption(option)}

        {subOptions.length > 0 && this.renderOptions(subOptions)}
      </Fragment>
    ));
  };

  renderOption = ({
    mobileRender,
    render,
    label,
    value,
    options,
    link,
    ...option
  } = {}) => {
    if (!render && !label && !value) {
      return null;
    }

    const optionIndex = this.optionsCount + 1;
    const optionRef = createRef();
    const optionProps = {
      ...option,
      'data-index': optionIndex,
      'data-value': value,
      'data-href': link,
      innerRef: optionRef,
      onClick: this.handleOptionSelect,
      onKeyDown: this.handleOptionKeyDown,
      onKeyUp: this.handleOptionKeyUp,
      tabIndex: 0,
      value,
    };

    if (render) {
      this.optionsCount = optionIndex;
      this.options[`${optionIndex}ref`] = optionRef;

      return render({
        label,
        ...optionProps,
      });
    }

    if (label && !value && value !== '' && value !== 0) {
      return <OptionsLabel>{label}</OptionsLabel>;
    }

    this.optionsCount = optionIndex;
    this.options[`${optionIndex}ref`] = optionRef;

    const { innerRef, ...rest } = optionProps;

    return (
      <Option ref={innerRef} {...rest}>
        {label}
      </Option>
    );
  };

  renderMobileOption = ({ label, link, value, mobileRender }) => {
    const { value: selectedValue } = this.props;

    if (mobileRender) {
      return mobileRender({
        label,
        link,
        value,
        selected: value === selectedValue,
      });
    }

    return (
      <option key={link} value={value} data-href={link} data-value={value}>
        {label}
      </option>
    );
  };

  renderMobileSelect = () => {
    const {
      options,
      name,
      resetOption,
      size,
      label,
      value,
      type,
      onRequestChange,
      ...props
    } = this.props;

    const selectedOption =
      options.find(({ value: optionValue }) => value === optionValue) || {};

    return (
      <Wrapper data-type={type}>
        {label && <FormFieldLabel as="span">{label}</FormFieldLabel>}

        <MobileSelect
          ref={this.mobileSelectRef}
          onChange={this.handleMobileSelectChange}
          name={name}
          size={size}
          value={selectedOption.value}
          data-href={selectedOption.link}
          data-value={selectedOption.value}
          {...props}
        >
          {resetOption && this.renderMobileOption(resetOption)}

          {options.map(this.renderMobileOption)}
        </MobileSelect>

        <ToggleButtonIcon mobile name="arrow" />
      </Wrapper>
    );
  };

  renderDesktopSelect = () => {
    const {
      options,
      groupOptions,
      defaultButtonLabel,
      buttonLabel,
      label,
      closeOnClickOutside,
      closeOnClickInside,
      name,
      value,
      onRequestChange,
      withAutoComplete,
      size,
      resetOption,
      optionsStyleType,
      isInnerAutoComplete,
      autoCompletePlaceholder,
      cleanBtnSettings,
      multiple,
      type,
      ...props
    } = this.props;

    const { isOpen, inputValue } = this.state;
    this.optionsCount = -1;

    const optionsToShow = this.getOptionsToShow(options);
    const groupOptionsToShow = this.getOptionsToShow(groupOptions);
    const { toggleLabel, hiddenLinksCount } = this.getToggleLabel(
      resetOption,
      groupOptionsToShow
    );

    return (
      <Wrapper
        {...props}
        data-type={type}
        onKeyUpCapture={this.handleWrapperKeyUp}
        onBlurCapture={this.handleBlur}
        ref={this.wrapperRef}
      >
        {label && <FormFieldLabel as="span">{label}</FormFieldLabel>}

        {withAutoComplete && !isInnerAutoComplete && isOpen && (
          <AutoCompleteInput
            value={inputValue}
            onChange={this.handleInputChange}
            placeholder={autoCompletePlaceholder}
            ref={this.autoCompleteInputRef}
            onKeyDown={this.handleInputKeyDown}
            autoFocus
          />
        )}

        <ToggleButton
          type="button"
          onClick={this.toggle}
          size={size}
          active={isOpen}
          onKeyUp={this.handleToggleBtnKeyUp}
          ref={this.toggleBtnRef}
        >
          <ToggleButtonLabel>{buttonLabel || toggleLabel}</ToggleButtonLabel>

          {hiddenLinksCount > 0 && (
            <HiddenLinks>
              <FormattedMessage id="select.andMore" />
              {` + ${hiddenLinksCount}`}
            </HiddenLinks>
          )}

          <ToggleButtonActions>
            {cleanBtnSettings.show && (
              <CleanBtn
                as={Link}
                to={cleanBtnSettings.link}
                href={cleanBtnSettings.link}
                onClick={e => e.stopPropagation()}
              />
            )}

            <ToggleButtonIcon name="arrow" />
          </ToggleButtonActions>
        </ToggleButton>

        <OptionsList
          isOpen={isOpen}
          onClick={closeOnClickInside ? this.close : null}
          onKeyDown={this.handleListKeyDown}
        >
          {withAutoComplete && isInnerAutoComplete && (
            <>
              <InnerAutoCompleteWrapper>
                <Icon name="search" />

                <InnerAutoCompleteInput
                  value={inputValue}
                  onChange={this.handleInputChange}
                  placeholder={autoCompletePlaceholder}
                  ref={this.autoCompleteInputRef}
                  onKeyDown={this.handleInputKeyDown}
                  autoFocus
                />
              </InnerAutoCompleteWrapper>

              <Divider width={2} color="green" />
            </>
          )}

          {resetOption && this.renderOption(resetOption)}

          {this.renderOptions(groupOptionsToShow)}

          {groupOptions.length > 0 && <Divider />}

          {this.renderOptions(optionsToShow)}
        </OptionsList>
      </Wrapper>
    );
  };

  render() {
    const { mobileSupport, isMobile } = this.props;
    return mobileSupport && isMobile
      ? this.renderMobileSelect()
      : this.renderDesktopSelect();
  }
}

Select.propTypes = {
  optionsStyleType: PropTypes.oneOf(['checkbox', 'option']),
  type: PropTypes.oneOf(['button', 'link']),
  name: PropTypes.string.isRequired,
  onRequestChange: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      render: PropTypes.func,
    })
  ),
  groupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
      render: PropTypes.func,
    })
  ),
  resetOption: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
    ]),
  }),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    ),
  ]).isRequired,
  defaultButtonLabel: PropTypes.node,
  autoCompletePlaceholder: PropTypes.string,
  buttonLabel: PropTypes.node,
  closeOnClickOutside: PropTypes.bool,
  closeOnClickInside: PropTypes.bool,
  withAutoComplete: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  multiple: PropTypes.bool,
  multipleValueSeparator: PropTypes.string,
  isInnerAutoComplete: PropTypes.bool,
  cleanBtnSettings: PropTypes.shape({
    show: PropTypes.bool,
    link: PropTypes.string,
  }),
  mobileSupport: PropTypes.bool,
  isMobile: PropTypes.bool,
};

Select.defaultProps = {
  optionsStyleType: 'option',
  type: 'button',
  onRequestChange: () => {},
  label: '',
  options: [],
  groupOptions: [],
  resetOption: null,
  defaultButtonLabel: '',
  buttonLabel: '',
  closeOnClickOutside: true,
  closeOnClickInside: true,
  withAutoComplete: false,
  size: 'medium',
  multiple: false,
  multipleValueSeparator: '',
  isInnerAutoComplete: false,
  cleanBtnSettings: {},
  autoCompletePlaceholder: '',
  mobileSupport: false,
  isMobile: false,
};

export default Select;
